'use client'

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import { getLocalizedSlug, type LocaleCode } from '@/lib/localized-slugs'

type Season = 'summer' | 'winter' | null

interface SeasonContextType {
  season: Season
  setSeason: (season: Season) => void
}

const SeasonContext = createContext<SeasonContextType | undefined>(undefined)

export function SeasonProvider({
  children,
  locale = 'en'
}: {
  children: React.ReactNode
  locale?: string
}) {
  const pathname = usePathname()

  // Get all winter slugs for the current locale
  const winterContactSlug = getLocalizedSlug('contact-winter', locale as LocaleCode)
  const winter7LauxSlug = getLocalizedSlug('7laux-winter', locale as LocaleCode)
  const locationWinterSlug = getLocalizedSlug('location-winter', locale as LocaleCode)
  const atelierWinterSlug = getLocalizedSlug('atelier-winter', locale as LocaleCode)

  // Get homepage slug for the current locale
  const homeSlug = getLocalizedSlug('7laux', locale as LocaleCode)

  // Initialize season based on current pathname - wrapped in useCallback
  const getSeasonFromPathname = useCallback((path: string | null) => {
    if (!path) return null

    // Remove locale prefix from pathname for comparison
    const pathWithoutLocale = path.replace(`/${locale}`, '') || '/'

    // Check if we're on the homepage
    const isHomePage = pathWithoutLocale === '/' || pathWithoutLocale === `/${homeSlug}`

    // Return null for homepage (no season selected)
    if (isHomePage) {
      return null
    }

    // Check all winter slugs
    const winterSlugs = [
      winterContactSlug,
      winter7LauxSlug,
      locationWinterSlug,
      atelierWinterSlug
    ]

    // Return winter if path includes any winter slug or '/winter'
    if (winterSlugs.some(slug => path.includes(slug)) || path.includes('/winter')) {
      return 'winter'
    }

    return 'summer'
  }, [locale, homeSlug, winterContactSlug, winter7LauxSlug, locationWinterSlug, atelierWinterSlug])

  const initialSeason = getSeasonFromPathname(pathname)
  const [season, setSeason] = useState<Season>(initialSeason)

  // Update season state when pathname changes
  useEffect(() => {
    const pathSeason = getSeasonFromPathname(pathname)
    setSeason(pathSeason)
  }, [pathname, getSeasonFromPathname])

  return (
    <SeasonContext.Provider value={{ season, setSeason }}>
      {children}
    </SeasonContext.Provider>
  )
}

export function useSeason() {
  const context = useContext(SeasonContext)
  if (context === undefined) {
    throw new Error('useSeason must be used within a SeasonProvider')
  }
  return context
}
